const state = {
  currentBattle: null,
  isLoading: false,
  battleStarted: false,
  currentEvent: null,
  eventIndex: 0,
  fighters: {
    fighter1: null,
    fighter2: null
  },
  winner: null,
  turnDuration: 5, // seconds per turn, adjustable
  currentAction: null,
  distance: null,
  battleTime: 0,
  battleEnded: false,
  speedMultiplier: 1,
  animationTimerId: null,  // Add this to track our animation timers
  activeSounds: {
    cheerSound: null,
    cheerSound2: null, // Second crowd sound for crossfading
    victorySound: null,
    announcerSound: null
  },
  isPlayerControllingFighter1: true // Track which fighter the player controls
}

const mutations = {
  SET_BATTLE(state, battle) {
    state.currentBattle = battle
  },
  SET_LOADING(state, loading) {
    state.isLoading = loading
  },
  SET_BATTLE_STARTED(state, started) {
    state.battleStarted = started
  },
  SET_CURRENT_EVENT(state, event) {
    state.currentEvent = event
  },
  SET_EVENT_INDEX(state, index) {
    state.eventIndex = index
  },
  SET_FIGHTERS(state, { fighter1, fighter2 }) {
    state.fighters = { fighter1, fighter2 }
  },
  SET_WINNER(state, winner) {
    state.winner = winner
  },
  UPDATE_FIGHTER_STATUS(state, { fighter, status }) {
    if (state.fighters[fighter]) {
      Object.assign(state.fighters[fighter], status)
    }
  },
  SET_TURN_DURATION(state, duration) {
    state.turnDuration = duration
  },
  SET_CURRENT_ACTION(state, action) {
    state.currentAction = action
  },
  SET_DISTANCE(state, distance) {
    state.distance = distance
  },
  SET_BATTLE_TIME(state, time) {
    state.battleTime = time
  },
  SET_BATTLE_ENDED(state, ended) {
    state.battleEnded = ended
  },
  RESET_BATTLE_STATE(state) {
    state.currentBattle = null;
    state.isLoading = false;
    state.battleStarted = false;
    state.currentEvent = null;
    state.eventIndex = 0;
    state.fighters = {
      fighter1: null,
      fighter2: null
    };
    state.winner = null;
    state.currentAction = null;
    state.distance = null;
    state.battleTime = 0;
    state.battleEnded = false;
  },
  SET_SPEED_MULTIPLIER(state, multiplier) {
    state.speedMultiplier = multiplier
  },
  SET_ANIMATION_TIMER_ID(state, id) {
    state.animationTimerId = id;
  },
  SET_ACTIVE_SOUND(state, { key, sound }) {
    state.activeSounds[key] = sound;
  },
  SET_PLAYER_FIGHTER(state, isControllingFighter1) {
    state.isPlayerControllingFighter1 = isControllingFighter1;
  }
}

const actions = {
  async initializeBattle({ commit }, battleData) {
    console.log('Initializing battle with data:', battleData);
    
    // Validate that we have fighters data
    if (!battleData?.match?.challenging_1s?.length || !battleData?.match?.challenging_2s?.length) {
      console.error('Invalid battle data:', battleData);
      throw new Error('Battle data is missing fighters');
    }

    // Handle both replay data and regular battle data
    let fighter1Health = 100;
    let fighter2Health = 100;
    let fighter1Stamina = 100;
    let fighter2Stamina = 100;
    let initialAction = null;

    // Try to get initial values from actions if available
    if (battleData.actions && battleData.actions.length > 0) {
      initialAction = battleData.actions.find(a => a.type === 'initial') || battleData.actions[0];
      fighter1Health = initialAction.fighter1Health ?? initialAction.health1 ?? fighter1Health;
      fighter2Health = initialAction.fighter2Health ?? initialAction.health2 ?? fighter2Health;
      fighter1Stamina = initialAction.fighter1Stamina ?? initialAction.stamina1 ?? fighter1Stamina;
      fighter2Stamina = initialAction.fighter2Stamina ?? initialAction.stamina2 ?? fighter2Stamina;
    }

    // Replays antigos sem ação inicial: usa os valores do lutador (que refletem o estado atual, não o do início da luta)
    if (!initialAction) {
      if (battleData.initialHealth1 !== undefined) fighter1Health = battleData.initialHealth1;
      if (battleData.initialHealth2 !== undefined) fighter2Health = battleData.initialHealth2;
      if (battleData.initialStamina1 !== undefined) fighter1Stamina = battleData.initialStamina1;
      if (battleData.initialStamina2 !== undefined) fighter2Stamina = battleData.initialStamina2;
    }

    const fighter1 = {
      ...battleData.match.challenging_1s[0],
      health: fighter1Health,
      stamina: fighter1Stamina,
      maxHealth: initialAction?.fighter1MaxHealth || battleData.match.challenging_1s[0].maxHealth || 100,
      maxStamina: initialAction?.fighter1MaxStamina || battleData.match.challenging_1s[0].maxStamina || 100
    };

    const fighter2 = {
      ...battleData.match.challenging_2s[0],
      health: fighter2Health,
      stamina: fighter2Stamina,
      maxHealth: initialAction?.fighter2MaxHealth || battleData.match.challenging_2s[0].maxHealth || 100,
      maxStamina: initialAction?.fighter2MaxStamina || battleData.match.challenging_2s[0].maxStamina || 100
    };

    commit('SET_DISTANCE', initialAction?.distance ?? null);

    console.log('Initializing battle with fighters:', {
      fighter1: `${fighter1.name} (${fighter1.health}hp)`,
      fighter2: `${fighter2.name} (${fighter2.health}hp)`
    });

    // If events are missing, create a simple event from the battle result
    if (!battleData.events || battleData.events.length === 0) {
      const winnerName = battleData.winner?.name || 
                         (battleData.victory === 1 ? fighter1.name : fighter2.name);
      
      battleData.events = [
        { text: `A luta começou entre ${fighter1.name} e ${fighter2.name}!` },
        { text: `Após uma batalha intensa, ${winnerName} saiu vitorioso!` }
      ];
    }

    // Ensure actions array exists
    if (!battleData.actions) {
      battleData.actions = [];
    }

    // Determine which fighter the player controls (assuming 'isPlayer' flag exists)
    // Default to fighter1 if we can't determine
    let isPlayerControllingFighter1 = true;
    
    // Check if there's any indication in the data about which fighter is controlled by player
    if (battleData.match) {
      // Most reliable way: check for an 'isPlayer' flag if it exists
      if (battleData.match.challenging_1s?.[0]?.isPlayer !== undefined) {
        isPlayerControllingFighter1 = !!battleData.match.challenging_1s[0].isPlayer;
      } 
      // Otherwise check if we're in a replay (player is likely spectating)
      else if (battleData.isReplay) {
        // In replays, we may have a preferred fighter to root for
        // If not specified, default to fighter1
        isPlayerControllingFighter1 = battleData.preferredFighter !== 2;
      }
      // Additional logic can be added here if needed
    }
    
    commit('SET_PLAYER_FIGHTER', isPlayerControllingFighter1);

    commit('SET_BATTLE', battleData);
    commit('SET_FIGHTERS', { fighter1, fighter2 });
    
    // If there's a winner already, set it
    if (battleData.winner) {
      commit('SET_WINNER', battleData.winner);
    }
  },

  async startBattleSequence({ commit, state, dispatch, rootGetters }) {
    // Play battle start sound
    dispatch('playBattleSound', 'start');
    
    // Start crowd cheering
    dispatch('playAmbientCrowd');
    
    commit('SET_BATTLE_STARTED', true);
    commit('SET_BATTLE_TIME', 0);
    
    await dispatch('playCountdown');
    
    // Safety check to prevent null errors
    if (!state.currentBattle || !state.currentBattle.events) {
      console.error('Cannot start battle: battle data or events are missing');
      commit('SET_BATTLE_ENDED', true);
      return;
    }
    
    const startTime = Date.now();
    const timerInterval = setInterval(() => {
      const elapsedTime = Math.floor(((Date.now() - startTime) * state.speedMultiplier) / 1000);
      commit('SET_BATTLE_TIME', elapsedTime);
    }, 1000 / state.speedMultiplier); // Ajusta o intervalo baseado na velocidade

    for (let i = 0; i < state.currentBattle.events.length; i++) {
      const event = state.currentBattle.events[i];
      const action = state.currentBattle.actions?.[i + 1];
      const nextAction = state.currentBattle.actions?.[i + 2]; // Get next action to check if it's the final one
      
      commit('SET_CURRENT_EVENT', event);
      
      // Ajuste o tempo de espera baseado no multiplicador de velocidade
      let waitPromise = new Promise(resolve => {
        const timerId = setTimeout(resolve, 500 / state.speedMultiplier);
        commit('SET_ANIMATION_TIMER_ID', timerId);
      });
      
      await waitPromise;
      
      if (action && action.type !== 'initial') {
        // Toda ação vai para a arena (ela decide a animação). O seq garante que ações repetidas animem de novo.
        commit('SET_CURRENT_ACTION', { ...action, seq: i });
        applyActionStatus(commit, state, action);

        try {
          await playActionSound({ state, dispatch, rootGetters }, action, nextAction);
        } catch (error) {
          // Som bloqueado pelo navegador não pode interromper o replay
          console.warn('Battle sound failed:', error);
        }
      } else {
        commit('SET_CURRENT_ACTION', null);
      }

      // Ajuste o tempo de espera entre eventos baseado no multiplicador de velocidade
      waitPromise = new Promise(resolve => {
        const timerId = setTimeout(resolve, (state.turnDuration * 1000) / state.speedMultiplier);
        commit('SET_ANIMATION_TIMER_ID', timerId);
      });
      
      await waitPromise;
    }

    clearInterval(timerInterval);
    // Ajuste o tempo da pausa dramática final baseado no multiplicador de velocidade
    await new Promise(resolve => {
      const timerId = setTimeout(resolve, 5000 / state.speedMultiplier);
      commit('SET_ANIMATION_TIMER_ID', timerId);
    });
    
    // Play appropriate end sounds based on winner
    dispatch('playBattleEndSounds');
    
    commit('SET_BATTLE_ENDED', true);
    commit('SET_WINNER', state.currentBattle.winner);
  },

  stopBattle({ commit, state, dispatch }) {
    // Stop all battle sounds
    dispatch('stopAllBattleSounds');
    
    // Cancel any pending timers
    if (state.animationTimerId) {
      clearTimeout(state.animationTimerId);
      commit('SET_ANIMATION_TIMER_ID', null);
    }
    
    // Clear current animation state
    commit('SET_CURRENT_ACTION', null);
    commit('SET_CURRENT_EVENT', null);
    
    // If we're in a replay, we can mark the battle as not started
    if (state.isReplay) {
      commit('SET_BATTLE_STARTED', false);
    }
  },

  // New sound-related methods
  async playBattleSound({ dispatch }, soundType) {
    return dispatch('SoundController/playSound', { 
      category: 'battle', 
      type: soundType,
      returnAudio: true
    }, { root: true });
  },
  
  // Improved crowd sound management
  async playAmbientCrowd({ commit, dispatch, rootGetters }) {
    dispatch('stopCrowdSounds');
    
    const cheerSound = await dispatch('SoundController/playSound', { 
      category: 'battle', 
      type: 'cheerFans',
      returnAudio: true
    }, { root: true });
    
    if (cheerSound) {
      const cheerSound2 = new Audio(cheerSound.src);
      
      // Mark these as ambient sounds so they're controlled by ambient volume
      cheerSound.dataset.soundType = 'ambient-battle';
      cheerSound2.dataset.soundType = 'ambient-battle';
      
      // Get volume from ambient volume setting
      const ambientVolume = rootGetters['SoundController/effectiveAmbient'];
      cheerSound.volume = cheerSound2.volume = ambientVolume;
      cheerSound.loop = cheerSound2.loop = false;
      
      commit('SET_ACTIVE_SOUND', { key: 'cheerSound', sound: cheerSound });
      commit('SET_ACTIVE_SOUND', { key: 'cheerSound2', sound: cheerSound2 });
      
      let isFirstPlaying = true;
      cheerSound.play();

      const handleTimeUpdate = function() {
        const timeLeft = this.duration - this.currentTime;
        if (timeLeft < 0.5) {
          const nextSound = isFirstPlaying ? cheerSound2 : cheerSound;
          if (nextSound.paused) {
            nextSound.currentTime = 0;
            nextSound.play().catch(console.error);
          }
        }
      };

      const handleEnded = function() {
        isFirstPlaying = !isFirstPlaying;
        this.currentTime = 0;
      };

      // Add event listeners to both sounds
      [cheerSound, cheerSound2].forEach(sound => {
        sound.addEventListener('timeupdate', handleTimeUpdate);
        sound.addEventListener('ended', handleEnded);
      });
      
      // Store cleanup function
      const cleanup = () => {
        [cheerSound, cheerSound2].forEach(sound => {
          sound.removeEventListener('timeupdate', handleTimeUpdate);
          sound.removeEventListener('ended', handleEnded);
        });
      };
      
      // Store cleanup function for later
      commit('SET_ACTIVE_SOUND', { key: 'crowdSoundCleanup', sound: cleanup });
    }
  },

  // For when volume is changed while playing
  async updateCrowdVolume({ state, rootGetters }) {
    if (state.activeSounds.cheerSound || state.activeSounds.cheerSound2) {
      const ambientVolume = rootGetters['SoundController/effectiveAmbient'];
      
      if (state.activeSounds.cheerSound) {
        state.activeSounds.cheerSound.volume = ambientVolume;
      }
      
      if (state.activeSounds.cheerSound2) {
        state.activeSounds.cheerSound2.volume = ambientVolume;
      }
    }
  },

  stopCrowdSounds({ state, commit }) {
    // Call cleanup function if it exists
    if (typeof state.activeSounds.crowdSoundCleanup === 'function') {
      state.activeSounds.crowdSoundCleanup();
    }

    // Stop both crowd sounds
    ['cheerSound', 'cheerSound2', 'crowdSoundCleanup'].forEach(key => {
      if (state.activeSounds[key]) {
        if (typeof state.activeSounds[key].pause === 'function') {
          state.activeSounds[key].pause();
          state.activeSounds[key].currentTime = 0;
        }
        commit('SET_ACTIVE_SOUND', { key, sound: null });
      }
    });
  },

  stopAllBattleSounds({ state, commit }) {
    // Stop all active sounds
    Object.keys(state.activeSounds).forEach(key => {
      const sound = state.activeSounds[key];
      // Check if it's an Audio object before calling pause
      if (sound instanceof Audio) {
        sound.pause();
        sound.currentTime = 0;
      } else if (typeof sound === 'function') {
        // If it's a cleanup function (like for crowd sounds), call it
        sound();
      }
      commit('SET_ACTIVE_SOUND', { key, sound: null });
    });
  },
  
  isPlayerWinner({ state }) {
    if (!state.winner) return false;
    
    const playerFighterId = state.isPlayerControllingFighter1 
      ? state.fighters.fighter1?.id 
      : state.fighters.fighter2?.id;
      
    return state.winner.id === playerFighterId;
  },
  
  isPlayerFighterDead({ state }) {
    const playerFighter = state.isPlayerControllingFighter1 
      ? state.fighters.fighter1 
      : state.fighters.fighter2;
      
    return playerFighter?.health <= 0;
  },

  async playBattleEndSounds({ state, commit, dispatch }) {
    // Stop the crowd sounds first
    if (state.activeSounds.cheerSound) {
      state.activeSounds.cheerSound.volume = 0.2; // Lower the volume but don't stop completely
    }
    
    const isPlayerWinner = await dispatch('isPlayerWinner');
    const isPlayerFighterDead = await dispatch('isPlayerFighterDead');
    
    if (isPlayerWinner) {
      // Play victory sounds
      // 1. Random victory cry (with equal chance)
      const victoryCries = ['victoryCry', 'victoryCry2', 'victoryCry3'];
      const randomCry = victoryCries[Math.floor(Math.random() * victoryCries.length)];
      
      const victorySound = await dispatch('playBattleSound', randomCry);
      if (victorySound) {
        victorySound.loop = true;
        victorySound.volume = 0.6;
        safePlay(victorySound);
        commit('SET_ACTIVE_SOUND', { key: 'victorySound', sound: victorySound });
      }
      
      // 2. Robot announcer (louder than cry)
      const roboticOptions = ['victoryRobotic', 'victoryRobotic2'];
      const randomRobotic = roboticOptions[Math.floor(Math.random() * roboticOptions.length)];
      
      const announcerSound = await dispatch('playBattleSound', randomRobotic);
      if (announcerSound) {
        announcerSound.volume = 0.8;
        safePlay(announcerSound);
        commit('SET_ACTIVE_SOUND', { key: 'announcerSound', sound: announcerSound });
      }
    } else {
      // Player lost
      if (isPlayerFighterDead) {
        // Play dead fighter announcement
        const deadSound = await dispatch('playBattleSound', 'deadFighter');
        if (deadSound) {
          deadSound.volume = 0.8;
          safePlay(deadSound);
          commit('SET_ACTIVE_SOUND', { key: 'announcerSound', sound: deadSound });
        }
      } else {
        // Play defeat announcement
        const defeatSound = await dispatch('playBattleSound', 'defeat');
        if (defeatSound) {
          defeatSound.volume = 0.8;
          safePlay(defeatSound);
          commit('SET_ACTIVE_SOUND', { key: 'announcerSound', sound: defeatSound });
        }
      }
      
      // Crying sound or F-word (10% chance)
      const useFWord = Math.random() < 0.1; // 10% chance
      
      if (useFWord) {
        const fSound = await dispatch('SoundController/playSound', {
          category: 'battle',
          type: 'fuck',
          returnAudio: true
        }, { root: true });
        
        if (fSound) {
          fSound.volume = 0.7;
          safePlay(fSound);
        }
      } else {
        const crySound = await dispatch('SoundController/playSound', {
          category: 'minigames',
          type: 'defeat',
          subtype: 'twoDefeat',
          returnAudio: true
        }, { root: true });
        
        if (crySound) {
          crySound.loop = true;
          crySound.volume = 0.6;
          safePlay(crySound);
          commit('SET_ACTIVE_SOUND', { key: 'victorySound', sound: crySound });
        }
      }
    }
  },
  
  async playCountdown() {
    return new Promise(resolve => {
      let count = 3
      const countInterval = setInterval(() => {
        if (count === 0) {
          clearInterval(countInterval)
          resolve()
        }
        // You can emit an event or update state for the countdown display
        count--
      }, 1000)
    })
  },

  resetBattle({ commit }) {
    commit('RESET_BATTLE_STATE');
  },

  setSpeedMultiplier({ commit }, multiplier) {
    commit('SET_SPEED_MULTIPLIER', multiplier)
  }
}

const CROWD_SOUND_KEYS = ['cheerSound', 'cheerSound2'];

// audio.play() rejeita quando o navegador bloqueia o som; isso não pode derrubar o replay
function safePlay(audio) {
  const result = audio?.play?.();
  if (result && typeof result.catch === 'function') {
    result.catch(error => console.warn('Battle sound blocked:', error));
  }
}

function fighterKeyOf(state, fighterId) {
  if (fighterId === state.fighters.fighter1?.id) return 'fighter1';
  if (fighterId === state.fighters.fighter2?.id) return 'fighter2';
  return null;
}

// Atualiza vida/stamina/distância a partir de qualquer ação (inclui formatos de replays antigos)
function applyActionStatus(commit, state, action) {
  const update = (fighterId, status) => {
    const fighter = fighterKeyOf(state, fighterId);
    if (fighter) commit('UPDATE_FIGHTER_STATUS', { fighter, status });
  };
  const rounded = value => (Number.isFinite(value) ? Math.round(value) : undefined);
  const defined = status => Object.fromEntries(Object.entries(status).filter(([, value]) => value !== undefined));

  switch (action.type) {
    case 'attack':
      update(action.defender, defined({ health: rounded(action.newHealth), stamina: rounded(action.defenderNewStamina) }));
      update(action.attacker, defined({ stamina: rounded(action.attackerNewStamina) }));
      break;
    case 'dodge':
      update(action.attacker, defined({ stamina: rounded(action.attackerNewStamina) }));
      update(action.defender, defined({ stamina: rounded(action.defenderNewStamina) }));
      break;
    case 'battle_end':
      if (action.subtype === 'death') update(action.loser, { health: 0 });
      break;
    default:
      // recovery, guard, move, talent e ações antigas (neutral/recovery, stamina_recovery, talent_effect)
      if (action.fighter !== undefined && Number.isFinite(action.newStamina)) {
        update(action.fighter, { stamina: Math.round(action.newStamina) });
      }
  }

  if (Number.isFinite(action.distance)) {
    commit('SET_DISTANCE', action.distance);
  }
}

async function playActionSound({ state, dispatch, rootGetters }, action, nextAction) {
  const baseVolume = rootGetters['SoundController/effectiveSound'];
  const pauseCrowd = () => CROWD_SOUND_KEYS.forEach(key => state.activeSounds[key]?.pause());
  const resumeCrowd = () => CROWD_SOUND_KEYS.forEach(key => {
    if (state.activeSounds[key]) safePlay(state.activeSounds[key]);
  });
  const playEffect = async (type, volume = baseVolume) => {
    const sound = await dispatch('playBattleSound', type);
    if (sound) {
      sound.volume = Math.min(volume, 1);
      safePlay(sound);
    }
    return sound;
  };
  // Torcida vibrando (talento ou golpe final): abafa a torcida ambiente enquanto toca
  const crowdEuphoria = async (resumeAfter = true) => {
    pauseCrowd();
    const sound = await playEffect('talentUse', baseVolume * 1.3);
    if (sound && resumeAfter) sound.addEventListener('ended', resumeCrowd);
  };

  const isShot = action.attackKind === 'shot' ||
    action.weaponTypes?.rightHand === 'gunWeapon' ||
    action.weaponTypes?.leftHand === 'gunWeapon';

  if (action.type === 'talent') {
    await crowdEuphoria();
    return;
  }

  if (action.type === 'dodge') {
    if (isShot) await playEffect('gunAttack');
    return;
  }

  if (action.type !== 'attack') return;

  const isKillingBlow = action.newHealth <= 0 && nextAction?.type === 'battle_end';
  if (isKillingBlow) {
    pauseCrowd();
    await playEffect('finalization');
  }

  await playEffect(isShot ? 'gunAttack' : (Math.random() < 0.5 ? 'handAttack' : 'handAttack2'));

  if (action.usedTalent || isKillingBlow) {
    await crowdEuphoria(!isKillingBlow);
  }
}

const getters = {
  getCurrentEvent: state => state.currentEvent,
  getBattleProgress: state => state.eventIndex / (state.currentBattle?.events.length || 1),
  getWinner: state => state.winner,
  getFighterStatus: state => fighter => state.fighters[fighter],
  getCurrentAction: state => state.currentAction,
  getDistance: state => state.distance,
  getBattleTime: state => {
    const minutes = Math.floor(state.battleTime / 60)
    const seconds = state.battleTime % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  },
  isBattleEnded: state => state.battleEnded,
  getSpeedMultiplier: state => state.speedMultiplier
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
